import { Button } from 'primereact/button';
import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import './profile.css';
import FlexComponent from '../FlexComponent';

const fmt = (v) => {
  if (v === null || v === undefined) return 'N/A';
  if (typeof v === 'object') return <pre style={{ margin: 0, fontSize: '0.8rem' }}>{JSON.stringify(v, null, 2)}</pre>;
  return String(v);
};

const formatSubmittedDate = (isoString) => {
  // console.log("isoString", isoString);

  if (!isoString) return 'N/A';
  const date = new Date(isoString);
  if (isNaN(date)) return 'N/A';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

const exportAllDataToCSV = (data) => {
  const rows = Array.isArray(data) ? data : [data];
  const csvRows = [];

  // Header for main fields
  const mainHeaders = [
    'ID', 'Name', 'Email', 'Phone', 'Est. Credit', 'Saved', 'Marital', 'Submitted', 'Source'
  ];
  csvRows.push(mainHeaders.join(','));

  // Process each submission
  rows.forEach(row => {
    const mainRow = [
      row.id || '',
      row.name || '',
      row.email || '',
      row.cell_phone_number || '',
      row.estimated_credit_score || '',
      row.total_amount_saved || '',
      row.marital_status || '',
      formatSubmittedDate(row.submitted_at) || '',
      row.lead_source || '',
    ];
    csvRows.push(mainRow.map(v => `"${v}"`).join(','));

    // === NESTED: Work History ===
    if (row.workhistory_set?.length) {
      csvRows.push(''); // blank line
      csvRows.push('"Work History"');
      const whHeaders = ['Title', 'Company', 'Yearly Income', 'Tenure', 'Emp. Type', 'Start', 'End', 'Address', 'City', 'State', 'ZIP', 'Notes'];
      csvRows.push(whHeaders.map(h => `"${h}"`).join(','));
      row.workhistory_set.forEach(wh => {
        const r = [
          wh.title || '',
          wh.company_name || '',
          wh.yearly_income || '',
          wh.time_at_position || '',
          wh.employment_type || '',
          wh.start_date || '',
          wh.end_date || '',
          wh.street_address || '',
          wh.city || '',
          wh.state || '',
          wh.zip_code || '',
          wh.work_history_description || '',
        ];
        csvRows.push(r.map(v => `"${v}"`).join(','));
      });
    }

    // === NESTED: Cosigner ===
    if (row.cosigner_set?.length) {
      csvRows.push('');
      csvRows.push('"Cosigner"');
      const coHeaders = ['First', 'Last', 'Email', 'Phone', 'Credit', 'Income', 'Title', 'Company', 'Street', 'City', 'State', 'ZIP'];
      csvRows.push(coHeaders.map(h => `"${h}"`).join(','));
      row.cosigner_set.forEach(co => {
        const r = [
          co.first_name || '',
          co.last_name || '',
          co.email || '',
          co.cell_phone_number || '',
          co.credit_score || '',
          co.yearly_income || '',
          co.title || '',
          co.company_name || '',
          co.home_address_street || '',
          co.home_address_city || '',
          co.home_address_state || '',
          co.home_address_zip || '',
        ];
        csvRows.push(r.map(v => `"${v}"`).join(','));
      });
    }

    // === NESTED: Property Owned ===
    if (row.propertyowned_set?.length) {
      csvRows.push('');
      csvRows.push('"Owned Property"');
      csvRows.push('"Rental Income","Notes"');
      row.propertyowned_set.forEach(po => {
        csvRows.push(`"${po.rental_income || ''}","${po.notes || ''}"`);
      });
    }

    // === NESTED: Shopping Areas ===
    if (row.shopping_set?.length) {
      csvRows.push('');
      csvRows.push('"Shopping Areas"');
      csvRows.push('"State","City","Country"');
      row.shopping_set.forEach(s => {
        csvRows.push(`"${s.state || ''}","${s.city || ''}","${s.country || ''}"`);
      });
    }

    // === NESTED: Other Income ===
    if (row.othersourceincome_set?.length) {
      csvRows.push('');
      csvRows.push('"Other Income"');
      csvRows.push('"Source","Period","Amount"');
      row.othersourceincome_set.forEach(oi => {
        csvRows.push(`"${oi.source || ''}","${oi.monthly_or_yearly || ''}","${oi.amount || ''}"`);
      });
    }

    // === NESTED: Initial Documents ===
    if (row.initial_documents_collected?.length) {
      csvRows.push('');
      csvRows.push('"Initial Documents"');
      row.initial_documents_collected.forEach(doc => {
        csvRows.push(`"${fmt(doc)}"`);
      });
    }

    csvRows.push(''); // separator between submissions
  });

  const csvContent = csvRows.join('\n');
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `submission_${rows[0]?.id || 'all'}_full_data.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

function AllSubmissionData({ data }) {
  const [expandedRows, setExpandedRows] = useState({});   // { id: true }
  const dt = useRef(null);

  useEffect(() => {
    const rows = Array.isArray(data) ? data : [data];
    const init = {};
    rows.forEach(r => { if (r.id !== undefined) init[r.id] = true; });
    setExpandedRows(init);
  }, [data]);

  const exportCSV = (selectionOnly) => {
    exportAllDataToCSV(data);
  };



  const mainColumns = [
    { field: 'name', header: 'Full Name', width: '180px' },
    { field: 'email', header: 'Email', width: '220px' },
    { field: 'cell_phone_number', header: 'Phone', width: '140px' },
    { field: 'estimated_credit_score', header: 'Est. Credit', width: '100px' },
    { field: 'total_amount_saved', header: 'Saved', width: '110px' },
    { field: 'marital_status', header: 'Marital', width: '80px' },
    {
      // no `field` → PrimeReact will NOT try to read the raw value
      header: 'Submitted',
      width: '180px',
      body: (row) => formatSubmittedDate(row.submitted_at)
    },
    { field: 'lead_source', header: 'Source', width: '180px' },
  ];

  const subTables = {
    workhistory_set: {
      title: 'Work History',
      columns: [
        { field: 'title', header: 'Title', width: '150px' },
        { field: 'company_name', header: 'Company', width: '160px' },
        { field: 'yearly_income', header: 'Yearly Income', width: '110px' },
        { field: 'time_at_position', header: 'Tenure', width: '120px' },
        { field: 'employment_type', header: 'Emp. Type', width: '130px' },
        { field: 'start_date', header: 'Start', width: '110px' },
        { field: 'end_date', header: 'End', width: '110px' },
        { field: 'street_address', header: 'Address', width: '200px' },
        { field: 'city', header: 'City', width: '120px' },
        { field: 'state', header: 'State', width: '80px' },
        { field: 'zip_code', header: 'ZIP', width: '90px' },
        { field: 'work_history_description', header: 'Notes', width: '180px' },
      ],
    },
    cosigner_set: {
      title: 'Cosigner',
      columns: [
        { field: 'first_name', header: 'First', width: '110px' },
        { field: 'last_name', header: 'Last', width: '110px' },
        { field: 'email', header: 'Email', width: '180px' },
        { field: 'cell_phone_number', header: 'Phone', width: '130px' },
        { field: 'credit_score', header: 'Credit', width: '90px' },
        { field: 'yearly_income', header: 'Income', width: '110px' },
        { field: 'title', header: 'Title', width: '130px' },
        { field: 'company_name', header: 'Company', width: '150px' },
        { field: 'home_address_street', header: 'Street', width: '200px' },
        { field: 'home_address_city', header: 'City', width: '120px' },
        { field: 'home_address_state', header: 'State', width: '80px' },
        { field: 'home_address_zip', header: 'ZIP', width: '90px' },
        { field: 'ghl_contact', header: 'GHL Contact', width: '200px' },
      ],
    },
    addresshistory_set: {
      title: 'Address History',
      columns: [
        { field: 'street', header: 'Street', width: '200px' },
        { field: 'city', header: 'City', width: '120px' },
        { field: 'state', header: 'State', width: '80px' },
        { field: 'zip', header: 'ZIP', width: '90px' },
        { field: 'from_date', header: 'From', width: '110px' },
        { field: 'to_date', header: 'To', width: '110px' },
      ],
    },
    propertyowned_set: {
      title: 'Owned Property',
      columns: [
        { field: 'rental_income', header: 'Rental Income', width: '120px' },
        { field: 'notes', header: 'Notes', width: '250px' },
      ],
    },
    shopping_set: {
      title: 'Shopping Areas',
      columns: [
        { field: 'state', header: 'State', width: '100px' },
        { field: 'city', header: 'City', width: '130px' },
        { field: 'country', header: 'Country', width: '120px' },
        // { field: 'priority', header: 'Priority', width: '90px'  },
      ],
    },
    othersourceincome_set: {
      title: 'Other Income Sources',
      columns: [
        { field: 'source', header: 'Source', width: '150px' },
        { field: 'monthly_or_yearly', header: 'Period', width: '100px' },
        { field: 'amount', header: 'Amount', width: '110px' },
      ],
    },
    initial_documents_collected: {
      title: 'Initial Documents',
      columns: [
        { field: '.', header: 'Document', body: (row) => fmt(row) },
      ],
    }
  };

  const rowExpansionTemplate = (row) => (
    <div style={{ padding: '1rem 0' }}>
      {Object.entries(subTables).map(([key, cfg]) => {
        const items = row[key] ?? [];
        if (!Array.isArray(items) || items.length === 0) return null;

        return (
          <div key={key} style={{ marginBottom: '2rem' }}>
            <h4 style={{ margin: '0 0 .5rem', fontWeight: 600 }}>{cfg.title}</h4>
            <DataTable value={items} size="small" showGridlines>
              {cfg.columns.map(col => (
                <Column
                  key={col.field}
                  field={col.field}
                  header={col.header}
                  body={col.body ? col.body : (r) => fmt(r[col.field])}
                  style={{ minWidth: col.width || '120px', width: col.width || 'auto' }}
                />
              ))}
            </DataTable>
          </div>
        );
      })}
    </div>
  );

  const tableData = Array.isArray(data) ? data : [data];

  return (
    <FlexComponent justifyContent="center" flexDirection="column" style={{ marginTop: "2rem" }}  >
      <FlexComponent justifyContent="end" style={{ paddingRight: ".5rem" }} >
        <Button type="button" icon="pi pi-file" rounded onClick={() => exportCSV(false)} data-pr-tooltip="CSV" />
      </FlexComponent>
      <DataTable
        ref={dt}
        value={tableData}
        expandedRows={expandedRows}
        onRowToggle={e => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
        dataKey="id"
        scrollable
        scrollHeight="100vh"
        showGridlines
        size="small"
        // tableStyle={{ maxWidth: '50%' }}
        style={{ width: "92vw" }}
      >

        <Column expander style={{ width: '50px', flex: '0 0 auto' }} />
        {mainColumns.map(c => (
          <Column
            key={c.field || c.header}
            field={c.field}
            header={c.header}
            body={c.body ? c.body : (row) => fmt(row[c.field])}
            style={{
              minWidth: c.width || '120px',
              width: c.width || 'auto',
              flex: c.width ? '0 0 auto' : '1 1 0',
            }}
          />
        ))}
      </DataTable>
    </FlexComponent>
  );
}

export default AllSubmissionData;